import { css } from "../../../../styled-system/css";
import { Box, Flex, Grid, VStack } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import { PageContainer } from "../../../components/core/basic/PageContainer";
import CustomHref from "../../../components/core/blocks/CustomHref";
import { useState } from "react";
import { useNavigate, Navigate, createFileRoute } from "@tanstack/react-router";
import { setChannel } from "../../../redux/reducers/channel";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getAccountsOptions } from "../../../modules/admin/api/useAccounts";
import { useAmazonOnboardingStatus } from "../../../modules/onboarding/amazon/useAmazonOnboardingStatus";
import { useOnboardingSummary } from "../../../modules/onboarding/amazon/useOnboardingSummary";
import Loading from "../../../components/core/blocks/Loading";
import { apiClient } from "../../../zodios/client";
import { initSpApiReauth, type SpApiRegion } from "../../../modules/onboarding/amazon/initSpApiOAuth";
import type { OnboardingSummary } from "../../../zodios/apiDefinitions/api/onboardingSummary";
import type { AmazonOnboardingStatus } from "../../../zodios/apiDefinitions/api/amazonOnboarding";
import {
  initAmazonAdvertisingOAuth,
  initAmazonAdvertisingReauth,
} from "../../../modules/onboarding/amazon/initAmazonAdvertisingOAuth";
import { DevErrorDisplay } from "../../../components/core/blocks/DevErrorDisplay";
import {
  MARKETPLACE_TO_COUNTRY_NAME,
  REGION_MARKETPLACE_LIST_MAP,
  useMarketplaceLabel,
} from "../../../utils/marketplaceConstants";
import { AmazonProfileSummary } from "../../../components/custom/onboarding/AmazonProfileSummary";
import ConnectSecondaryChannelWidget from "../../../modules/onboarding/components/ConnectSecondaryChannelWidget";
import AdditionalWalmartOrgModal from "../../../components/custom/onboarding/AdditionalWalmartOrgModal";
import { successToast, errorToast } from "../../../utils/toasts";
import { useSimpleCan } from "../../../config/casl/casl";
import UnauthorizedWarning from "../../../components/core/basic/UnauthorizedWarning";
import InvalidAdsProfilesWarning from "../../../components/custom/onboarding/InvalidAdsProfilesWarning";
import { ShopifySecondaryChannelLink } from "../../../modules/onboarding/shopify/ShopifySecondaryChannelLink";
import { useAppSelector, useAppDispatch } from "../../../redux/hooks";
import { useTranslation } from "react-i18next";

const mobileButtonContainerStyles = css({
  justifyContent: { base: "center", sm: "flex-end" },
  textAlign: { sm: "end" },
  margin: { base: "8rem 0", sm: "5rem 10rem 5rem 0" },
});

const overflowHiddenStyles = css({
  maxHeight: "100vh",
  overflow: "auto",
  smDown: { overflowX: "hidden", overflowY: "auto" },
});

const titleStyles = css({ margin: 0, color: "#393939", fontSize: "34px", fontWeight: 700 });
const subtitleStyles = css({ margin: 0, fontSize: "medium" });
const sectionHeadingStyles = css({ margin: 0, fontSize: "26px", fontWeight: 600 });
const noTransformStyles = css({ transform: "none" });
const refreshLinkStyles = css({ fontSize: "14px", fontWeight: 500, cursor: "pointer" });

const REGION_SELLER_DOMAIN: Record<SpApiRegion, string> = {
  NA: "www.amazon.com",
  EU: "www.amazon.co.uk",
  FE: "www.amazon.com.au",
};

const REGION_SELLER_ID_KEY = {
  NA: "amazon_mws_seller_id",
  EU: "eu_amazon_mws_seller_id",
  FE: "fe_amazon_mws_seller_id",
} as const satisfies Record<SpApiRegion, keyof OnboardingSummary>;

const REGION_SPAPI_ACCESS_KEY = {
  NA: "amazon_spapi_access",
  EU: "eu_amazon_spapi_access",
  FE: "fe_amazon_spapi_access",
} as const satisfies Record<SpApiRegion, keyof AmazonOnboardingStatus>;

function computeAdvertisingMarketplaceCodes(profileIds: Record<string, Record<string, string>>): string[] {
  if (!Object.keys(profileIds).length) return [];
  const seen = new Set<string>();
  const codes: string[] = [];
  for (const code of Object.keys(MARKETPLACE_TO_COUNTRY_NAME)) {
    if (code in profileIds) {
      const name = MARKETPLACE_TO_COUNTRY_NAME[code as keyof typeof MARKETPLACE_TO_COUNTRY_NAME];
      if (!seen.has(name)) {
        seen.add(name);
        codes.push(code);
      }
    }
  }
  return codes;
}

function computeAdvertisingRegionCodes(regions: string[] | undefined): SpApiRegion[] {
  if (!regions) return [];
  return regions.filter((r): r is SpApiRegion => r === "NA" || r === "EU" || r === "FE");
}

function selectOnboardData(data: OnboardingSummary) {
  return {
    ...data,
    advertisingMarketplaceCodes: computeAdvertisingMarketplaceCodes(data.amazon_profile_ids),
    advertisingRegionCodes: computeAdvertisingRegionCodes(data.account_region),
  };
}

function RegionSellerSection({ region }: { region: SpApiRegion }) {
  const { t } = useTranslation(["onboarding"]);
  const user = useAppSelector((state) => state.user);
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const onboardingStatusQuery = useAmazonOnboardingStatus();
  const onboardDataQuery = useOnboardingSummary();

  const isSeller = user.organization_type === "seller";
  const showRefreshListings = user.organization_name !== "WP-Unilever";
  const sellerId = onboardDataQuery.data?.[REGION_SELLER_ID_KEY[region]];
  const spapiAccessStatus = onboardingStatusQuery.data?.[REGION_SPAPI_ACCESS_KEY[region]];

  const refreshListings = useMutation({
    mutationFn: async (r: "eu" | "na" | "fe") => {
      return await apiClient.refreshProductListing({
        custom_api_request: "refresh_product_listing",
        region: r,
      });
    },
    onSuccess: () => {
      successToast({ message: t(($) => $.onboarding["Refreshing your Amazon product listings."]) });
    },
    onError: (error: unknown) => {
      errorToast({ message: t(($) => $.onboarding["Failed to refresh Amazon product listings."]), error });
    },
  });

  function getFirstSellerSubscribedMarketplaceInRegion(): string | null {
    if (!(region in REGION_MARKETPLACE_LIST_MAP)) return null;
    const marketplaces = REGION_MARKETPLACE_LIST_MAP[region as keyof typeof REGION_MARKETPLACE_LIST_MAP];
    if (marketplaces.includes(marketPlace)) return marketPlace;

    const sellerMps = onboardDataQuery.data?.amazon_seller_marketplaces;
    const match = marketplaces.find((code: string) => sellerMps?.includes(code));
    if (match === "USA") return "US";
    return match ?? null;
  }

  function editSpApiAccess(): void {
    let use_marketplace = getFirstSellerSubscribedMarketplaceInRegion();
    if (use_marketplace === "USA") {
      use_marketplace = "US";
    }
    const profileType = onboardDataQuery.data!.amazon_profile_ids[use_marketplace!]?.["type"];
    const is_seller = profileType !== "vendor";
    initSpApiReauth(region, use_marketplace!, is_seller);
  }

  const isNotAvailable = spapiAccessStatus === "not_available" || spapiAccessStatus === "pending";
  const isConnected = spapiAccessStatus === "connected";
  const sellerLink = `https://${REGION_SELLER_DOMAIN[region]}/sp?seller=${sellerId}`;

  return (
    <Box
      display="grid"
      gap="medium"
      alignItems="center"
      gridTemplateColumns={{ base: "1fr", lg: "5fr 1fr" }}
      marginTop="2rem"
      fontSize="18px"
      fontWeight={500}
    >
      <Grid gap="medium" columns={{ base: 1, sm: 2 }}>
        <span>
          {region === "NA" ?
            t(($) => $.onboarding["North America Seller ID:"])
          : region === "EU" ?
            t(($) => $.onboarding["Europe Seller ID:"])
          : t(($) => $.onboarding["Far East Seller ID:"])}
        </span>
        <span className={css({ fontWeight: 300 })}>
          {isNotAvailable ?
            t(($) => $.onboarding["Not available"])
          : isSeller ?
            <CustomHref text={sellerId} link={sellerLink} type="target" />
          : sellerId}
        </span>
      </Grid>
      <VStack gap="xsmall" alignItems="end" marginTop={isConnected ? "1rem" : undefined}>
        <Button className={noTransformStyles} onPress={editSpApiAccess}>
          {isNotAvailable ? t(($) => $.onboarding["Authorize"]) : t(($) => $.onboarding["Reauthorize"])}
        </Button>
        {showRefreshListings && (
          <span
            className={refreshLinkStyles}
            onClick={() => refreshListings.mutate(region.toLowerCase() as "na" | "eu" | "fe")}
          >
            <CustomHref text={t(($) => $.onboarding["Refresh Listings"])} />
          </span>
        )}
      </VStack>
    </Box>
  );
}

function AmazonSummary() {
  const { t } = useTranslation(["onboarding"]);
  const getMarketplaceLabel = useMarketplaceLabel();
  const dispatch = useAppDispatch();
  const onboardingStatusQuery = useAmazonOnboardingStatus();
  const navigate = useNavigate();

  const user = useAppSelector((state) => state.user);
  const channel = useAppSelector((state) => state.channel);

  const canCreateOrg = useSimpleCan("create", "Org");

  const onboardDataQuery = useOnboardingSummary({
    select: selectOnboardData,
  });

  const noSupportedProfilesAvailable =
    onboardingStatusQuery.data?.amazon_advertising_access === "connected" &&
    (Object.keys(onboardDataQuery.data?.amazon_profile_ids ?? {}).every((key) => key.includes("_vendor")) ||
      !Object.keys(onboardDataQuery.data?.amazon_profile_ids ?? {}).length);

  const [showWalmartOrgModal, setShowWalmartOrgModal] = useState(false);

  const walmartAccountsQuery = useQuery(getAccountsOptions("walmart"));
  const walmartProfileExists = !!(
    walmartAccountsQuery.data &&
    (Object.keys(walmartAccountsQuery.data.seller).length || Object.keys(walmartAccountsQuery.data.vendor).length)
  );

  if (channel.currentChannel === "walmart") {
    return <Navigate to="/user/walmartsummary" replace />;
  }

  if (onboardDataQuery.isPending) {
    return <Loading />;
  }

  if (onboardDataQuery.error) {
    return (
      <DevErrorDisplay
        userMessage={t(($) => $.onboarding["Error loading onboarding summary"])}
        error={onboardDataQuery.error}
      />
    );
  }

  if (onboardingStatusQuery.error) {
    return (
      <DevErrorDisplay
        userMessage={t(($) => $.onboarding["Error loading onboarding status"])}
        error={onboardingStatusQuery.error}
      />
    );
  }

  const sellerAccountRegionLabel = onboardDataQuery.data.advertisingRegionCodes
    .map((r) =>
      r === "NA" ? t(($) => $.onboarding.regions["North America"])
      : r === "EU" ? t(($) => $.onboarding.regions.Europe)
      : t(($) => $.onboarding.regions["Far East"]),
    )
    .join(", ");

  const advertisingMarketplacesLabel = onboardDataQuery.data.advertisingMarketplaceCodes
    .map(getMarketplaceLabel)
    .join(", ");

  return (
    <>
      {canCreateOrg ?
        <PageContainer paddingX="xxlarge" className={overflowHiddenStyles}>
          <VStack gap="medium" paddingTop="xlarge" textAlign="center">
            <h1 className={titleStyles}>
              {channel.currentChannel === "amazon" ?
                t(($) => $.onboarding["Amazon Onboarding"])
              : t(($) => $.onboarding["Walmart Onboarding"])}
            </h1>
            <p className={subtitleStyles}>
              {t(($) => $.onboarding["Please confirm your following information is correct"])}
            </p>
          </VStack>

          <VStack gap="large" alignItems="stretch">
            <Box
              display="grid"
              gap="medium"
              alignItems="center"
              gridTemplateColumns={{ base: "1fr", lg: "3fr 1fr" }}
              borderBottom="1px solid {colors.border.default}"
              paddingY="large"
            >
              <VStack gap="small" alignItems="stretch">
                <h3 className={sectionHeadingStyles}>
                  {channel.currentChannel === "amazon" ?
                    t(($) => $.onboarding["Amazon Advertising Account"])
                  : t(($) => $.onboarding["Walmart Advertising Account"])}
                </h3>
                {noSupportedProfilesAvailable ?
                  <InvalidAdsProfilesWarning />
                : <>
                    <Grid gap="medium" columns={{ base: 1, sm: 2 }} fontSize="18px" fontWeight={500}>
                      <span>{t(($) => $.onboarding["Region(s):"])}</span>
                      <span className={css({ fontWeight: 300 })}>{sellerAccountRegionLabel}</span>
                    </Grid>
                    <Grid gap="medium" columns={{ base: 1, sm: 2 }} fontSize="18px" fontWeight={500}>
                      <span>{t(($) => $.onboarding["Your Available Advertising Marketplace(s):"])}</span>
                      <span className={css({ fontWeight: 300 })}>{advertisingMarketplacesLabel}</span>
                    </Grid>
                  </>
                }
              </VStack>
              <Button
                onPress={() => {
                  if (onboardingStatusQuery.data?.amazon_advertising_access === "connected") {
                    initAmazonAdvertisingReauth();
                  } else {
                    initAmazonAdvertisingOAuth();
                  }
                }}
                className={css({
                  alignSelf: "center",
                  justifySelf: "end",
                })}
                isDisabled={user.is_staff}
              >
                {onboardingStatusQuery.data?.amazon_advertising_access !== "connected" ?
                  t(($) => $.onboarding["Authorize"])
                : t(($) => $.onboarding["Reauthorize"])}
              </Button>
            </Box>

            {!noSupportedProfilesAvailable && (
              <VStack
                gap="small"
                alignItems="stretch"
                borderBottom="1px solid {colors.border.default}"
                paddingY="large"
              >
                <h3 className={sectionHeadingStyles}>
                  {channel.currentChannel === "amazon" ?
                    t(($) => $.onboarding["SP-API Account"])
                  : t(($) => $.onboarding["Account"])}
                </h3>

                {onboardDataQuery.data.account_region?.includes("NA") && <RegionSellerSection region="NA" />}
                {onboardDataQuery.data.account_region?.includes("EU") && <RegionSellerSection region="EU" />}
                {onboardDataQuery.data.account_region?.includes("FE") && <RegionSellerSection region="FE" />}
              </VStack>
            )}

            <Box borderBottom="1px solid {colors.border.default}" paddingBottom="large">
              <AmazonProfileSummary />
            </Box>

            <VStack gap="medium" margin="3rem 0" alignItems="stretch">
              <Flex>
                <ConnectSecondaryChannelWidget
                  secondaryChannel={"walmart"}
                  linkToSummaryView={walmartProfileExists ? "/user/walmartsummary" : undefined}
                  onClick={() => {
                    if (walmartProfileExists) {
                      dispatch(setChannel("walmart"));
                      localStorage.setItem("channel", "walmart");
                    } else {
                      setShowWalmartOrgModal(true);
                    }
                  }}
                />
                <AdditionalWalmartOrgModal
                  isOpen={showWalmartOrgModal}
                  onOpenChange={(open: boolean) => setShowWalmartOrgModal(open)}
                />
              </Flex>
              <ShopifySecondaryChannelLink />
            </VStack>

            <Flex className={mobileButtonContainerStyles}>
              <Button
                onPress={() => {
                  void navigate({ to: "/user/dashboard" });
                }}
              >
                {t(($) => $.onboarding["Go to Dashboard"])}
              </Button>
            </Flex>
          </VStack>
        </PageContainer>
      : <UnauthorizedWarning />}
    </>
  );
}

export const Route = createFileRoute("/_authenticated/user/amzsummary")({
  component: AmazonSummary,
  staticData: { application: "Admin" },
});
